'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

// Persisted once the user dismisses (X or Continue Browsing) so the toast is
// shown only once per user (across sessions), not once per session.
const STORAGE_KEY = 'direct-rate-toast-dismissed'
const TIME_TRIGGER_MS = 15000 // 15 seconds
const SCROLL_TRIGGER_PERCENT = 50 // 50% scroll

export function DirectRateToast() {
  const [isVisible, setIsVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const hasTriggeredRef = useRef(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Check if user has ever explicitly dismissed the toast
  const wasDismissed = useCallback(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    }
    return false
  }, [])

  const showToast = useCallback(() => {
    if (hasTriggeredRef.current) return
    if (wasDismissed()) return

    hasTriggeredRef.current = true
    setIsVisible(true)
  }, [wasDismissed])

  // Called when user explicitly dismisses (X button or Continue Browsing)
  const dismissToast = useCallback(() => {
    setIsClosing(true)
    
    // Mark as dismissed permanently for this user (persists across sessions)
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true')
    }
    
    setTimeout(() => {
      setIsVisible(false)
      setIsClosing(false)
    }, 400)
  }, [])

  const handleContinueBrowsing = useCallback(() => {
    dismissToast()
  }, [dismissToast])

  // Handle scroll trigger
  useEffect(() => {
    const handleScroll = () => {
      if (hasTriggeredRef.current) return

      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrolled = window.scrollY
      const scrollPercent = (scrolled / scrollHeight) * 100

      if (scrollPercent >= SCROLL_TRIGGER_PERCENT) {
        showToast()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [showToast])

  // Handle time trigger
  useEffect(() => {
    // Don't start timer if already dismissed this session
    if (wasDismissed()) return

    timerRef.current = setTimeout(() => {
      showToast()
    }, TIME_TRIGGER_MS)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [showToast, wasDismissed])

  // Cleanup timer when toast is shown
  useEffect(() => {
    if (isVisible && timerRef.current) {
      clearTimeout(timerRef.current)
    }
  }, [isVisible])

  if (!isVisible) return null

  return (
    <div
      className={`
        fixed bottom-5 z-[1000] overflow-hidden box-border
        bg-gradient-to-br from-white to-slate-50
        backdrop-blur-sm border border-white/20
        shadow-[0_20px_40px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.05)]
        rounded-2xl p-3.5 px-[18px]
        font-sans
        transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]
        ${isClosing 
          ? 'right-[-500px] max-md:translate-x-[calc(100%+20px)]' 
          : 'right-5 direct-rate-toast-show max-md:right-2.5 max-md:left-2.5 max-md:translate-x-0'
        }
        w-[450px] max-w-[450px]
        max-md:w-auto max-md:max-w-none max-md:p-3 max-md:bg-gradient-to-br max-md:from-white max-md:to-slate-50
        max-md:shadow-[0_10px_30px_rgba(0,0,0,0.15),0_0_1px_rgba(0,0,0,0.1)]
        max-[360px]:p-2.5 max-[360px]:bottom-2 max-[360px]:rounded-xl
      `}
      role='alert'
      aria-live='polite'
    >
      {/* Gradient accent bar */}
      <div className='absolute top-0 left-0 right-0 h-1 direct-rate-toast-accent rounded-t-2xl max-md:h-[3px]' />

      <button
        className='
          absolute top-3 right-3
          bg-black/5 border-none
          text-xl cursor-pointer text-gray-500
          w-7 h-7 rounded-full
          flex items-center justify-center
          transition-all duration-200 ease-in-out
          hover:bg-black/10 hover:text-gray-700 hover:scale-110
          active:scale-95
          max-md:top-2 max-md:right-2 max-md:bg-black/[0.06]
          max-md:active:scale-90 max-md:active:bg-black/15
          max-[360px]:w-[26px] max-[360px]:h-[26px] max-[360px]:text-lg max-[360px]:top-1.5 max-[360px]:right-1.5
        '
        onClick={dismissToast}
        aria-label='Close notification'
        type='button'
      >
        ×
      </button>

      <div className='flex items-center mb-2 max-md:mb-1.5 max-[360px]:mb-1'>
        <h4 className='
          m-0 text-[#000D33] text-base font-semibold tracking-tight
          break-words flex-1
          max-md:text-sm max-md:leading-tight max-md:pr-[30px]
          max-[360px]:text-[13px] max-[360px]:pr-[26px]
        '>
          Save 10% Automatically!{' '}
          <span className='inline-block direct-rate-toast-emoji'>💸</span>
        </h4>
      </div>

      <p className='
        mt-2 mb-0 text-[#191919]/70 text-[13px] leading-relaxed font-normal
        break-words
        max-md:text-xs max-md:mt-1.5
        max-[360px]:text-[11px] max-[360px]:leading-snug max-[360px]:mt-1
      '>
        Did you know? Booking here is always cheaper. We&apos;ve discounted every unit by{' '}
        <strong className='text-[#000D33] font-bold'>10%</strong> compared to other channels just for visiting us directly.
      </p>

      <button
        className='
          inline-block mt-2.5 py-2 px-5
          bg-[#000D33]
          text-white border-none rounded-lg
          text-[13px] font-semibold text-center
          cursor-pointer
          transition-all duration-300 ease-in-out
          shadow-[0_4px_12px_rgba(0,13,51,0.3)]
          hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(0,13,51,0.4)]
          hover:bg-[#001a4d]
          active:translate-y-0 active:shadow-[0_2px_8px_rgba(0,13,51,0.3)]
          max-md:mt-2.5 max-md:py-2.5 max-md:px-4 max-md:text-xs
          max-[360px]:mt-2 max-[360px]:py-2 max-[360px]:px-3.5 max-[360px]:text-[11px] max-[360px]:rounded-[7px]
        '
        onClick={handleContinueBrowsing}
        type='button'
      >
        Continue Browsing
      </button>
    </div>
  )
}
